import { createElement, forwardRef, useRef, useState } from 'react';
import { getLogger } from '@sas/nova-core/logging';
import cx from 'classnames';
import { HeadingVariant } from '.';
import { useResizeObserverRef } from '../../hooks/useResizeObserver';
import { isTruncated, useTruncationTooltip } from '../../hooks/useTruncationTooltip';
import { useGetDirection } from '../../locale/useBidiFormatter';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { setRef } from '../../util/ref-util';
import { useDensity } from '../Density';
import { Icon } from '../Icon';
import { GlobalSettingsKeys } from '../Settings/GlobalSettingsKeys';
import { useGetGlobalSetting } from '../Settings/GlobalSettingsProvider';
import cn from './Heading.css.json';
import './Heading.css';
import { jsx as _jsx } from "react/jsx-runtime";
const logger = getLogger('Heading');
export const Heading = /*#__PURE__*/forwardRef(function Heading(props, ref) {
  const {
    className,
    style,
    level,
    variant,
    children,
    wrapText,
    icon,
    focusable,
    testId,
    disabled,
    dir,
    textProps,
    ...baseHeadingProps
  } = props;
  const dt = useDesignTokens();
  const areKeyboardTooltipsEnabled = useGetGlobalSetting(GlobalSettingsKeys.ENABLE_KEYBOARD_TOOLTIPS) ?? 'false';
  const shouldFocusTruncatedHeadings = useGetGlobalSetting(GlobalSettingsKeys.FOCUS_TRUNCATED_HEADINGS) ?? 'false';
  let headingTokens = {
    fg: disabled ? dt.tokens['color-action-text-standard-disabled'] : dt.tokens['color-heading-text'],
    focus: 'var(--focus-light-bg)'
  };
  if (dt.themeVariant === 'light' && variant === HeadingVariant.PrimaryLight) {
    headingTokens = {
      fg: disabled ? dt.tokens['color-action-text-emphasis-disabled'] : dt.tokens['color-text-primary-inverse'],
      focus: 'var(--focus-dark-bg)'
    };
  }
  const {
    density
  } = useDensity();
  const headingStyle = renderInlineCssVars({
    ...headingTokens,
    'font-weight-default': dt.tokens['font-style-weight-400'],
    'font-family': dt.tokens['font-family-secondary'],
    'line-height': dt.tokens['font-space-line-height'],
    'font-size': density && density === 'high' ? dt?.tokensExperimental['font-size-body-text-s'] : dt.tokens[`font-size-heading-text-${level}`]
  }, 'heading');
  const headingRef = useRef(null);
  const textRef = useRef(null);
  const setTruncationRef = useTruncationTooltip({
    truncateRef: textRef
  });
  const direction = useGetDirection(children);
  const [defaultFocusable, setDefaultFocusable] = useState(false);
  const shouldUseDefaultFocusable = focusable === undefined && !wrapText && areKeyboardTooltipsEnabled === 'true' && shouldFocusTruncatedHeadings === 'true';
  const [setResizeObserverRef] = useResizeObserverRef(() => {
    if (!shouldUseDefaultFocusable) return;
    setDefaultFocusable(textRef.current !== null && isTruncated(textRef.current));
  });
  if (![1, 2, 3, 4, 5, 6].includes(level)) {
    // eslint-disable-next-line @sas/i18n/jsx-to-localize
    logger.error(`Heading level must be an integer between 1 and 6 (current value: ${level})`);
    return null;
  }
  return /*#__PURE__*/createElement(`h${level}`, {
    ...baseHeadingProps,
    className: cx(cn.heading, className, !wrapText && cn.nowrap),
    style: {
      ...headingStyle,
      ...style
    },
    'aria-disabled': disabled ? true : undefined,
    'data-testid': testId,
    tabIndex: focusable ?? (shouldUseDefaultFocusable && defaultFocusable) ? 0 : undefined,
    ref: e => {
      setRef(ref, e);
      setRef(setResizeObserverRef, e);
      headingRef.current = e;
      if (!wrapText) {
        setTruncationRef(e);
      }
    }
  }, icon ? /*#__PURE__*/_jsx(Icon, {
    ...icon,
    className: cx(cn.icon, !children ? cn.iconOnly : undefined, icon.className),
    disabled: disabled
  }) : undefined, children ? /*#__PURE__*/_jsx("span", {
    ...textProps,
    className: cx(cn.text, textProps?.className),
    ref: textRef,
    dir: textProps?.dir ?? direction,
    children: children
  }) : undefined);
});
export default Heading;
//# sourceMappingURL=Heading.js.map